// src/modules/app-views/crm/pages/Kanban/components/modals/ColumnPropertiesModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useState } from 'react';
import { Form, Modal, Tabs } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import crmService from '../../../../../service';
import ParametersTab from './tabs/ParametersTab';
import AutomationTab from './tabs/AutomationTab';
import MetricsTab from './tabs/MetricsTab';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ColumnPropertiesModal = ({ show, setShow, column, boardUid }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const colUid = column?.uid || column?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('parameters');
  const [saving, setSaving] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (show && column) {
      // Read fresh column data from the service to avoid stale prop after save.
      // The parent Kanban board may hold a cached column reference that doesn't
      // reflect the latest save; the service store is always up-to-date.
      const freshColumns = crmService.getColumnsByBoardUid(boardUid);
      const freshCol = freshColumns.find(c => (c.uid || c._id) === colUid) || column;

      const sparks = freshCol.automation?.sparks;
      const sla = freshCol.automation?.sla;

      form.setFieldsValue({
        title: freshCol.title,
        color: freshCol.color || '#8c8c8c',
        description: freshCol.description || '',
        cardOrder: freshCol.cardOrder || 'oldest_first',
        sparks_operation: sparks?.operation ?? '',
        sparks_amount: sparks?.amount ?? 0,
        sla: loadSla(sla),
      });
    }
  }, [show, column, form, boardUid, colUid]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (!saving) setShow(false);
  }, [saving, setShow]);

  const handleAfterClose = useCallback(() => {
    setActiveTab('parameters');
    setSaving(false);
    form.resetFields();
  }, [form]);

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const color =
        typeof values.color === 'string'
          ? values.color
          : values.color?.toHexString?.() || '#8c8c8c';

      setSaving(true);
      await crmService.updateColumn(boardUid, colUid, {
        title: values.title.trim(),
        color,
        description: (values.description || '').trim() || null,
        cardOrder: values.cardOrder || 'oldest_first',
        automation: {
          sparks: {
            operation: values.sparks_operation || null,
            amount: values.sparks_operation ? Math.max(1, values.sparks_amount || 1) : 0,
          },
          sla: buildSlaPayload(values.sla),
        },
      });
      appService.message({ flag: 's', text: 'saved_successfully' });
      setShow(false);
    } catch (err) {
      // Auto-navigate to the first tab that has a validation error
      const firstErrorField = err?.errorFields?.[0]?.name?.[0];
      if (firstErrorField) {
        if (['title', 'color'].includes(firstErrorField)) setActiveTab('parameters');
        else if (['sparks_operation', 'sparks_amount'].includes(firstErrorField))
          setActiveTab('automation');
        else if (firstErrorField === 'sla') setActiveTab('metrics');
      }
    } finally {
      setSaving(false);
    }
  }, [boardUid, colUid, form, setShow]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // forceRender ensures all Form.Items are mounted and registered with the form instance
  // even when the tab has never been activated. Without this, inactive tabs' Form.Items
  // are lazily rendered, so form.validateFields() returns undefined for their fields —
  // causing those values (e.g. sla_businessTime) to be silently cleared on save.
  const tabItems = [
    {
      key: 'parameters',
      label: translateX('parameters'),
      children: <ParametersTab form={form} />,
      forceRender: true,
    },
    {
      key: 'metrics',
      label: translateX('metrics'),
      children: <MetricsTab form={form} />,
      forceRender: true,
    },
    {
      key: 'automation',
      label: translateX('automation'),
      children: <AutomationTab form={form} />,
      forceRender: true,
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SettingOutlined />
          {translateX('properties')}
        </span>
      }
      open={show}
      onCancel={handleClose}
      afterClose={handleAfterClose}
      onOk={handleSave}
      okText={translateX('save')}
      cancelText={translateX('cancel')}
      confirmLoading={saving}
      mask={{ closable: !saving }}
      destroyOnHidden
      width={420}
      styles={{
        body: {
          maxHeight: 'calc(80vh - 110px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
      style={{ top: '10vh' }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        className="parameters-modal-tabs"
      />
      <style>{`
        .parameters-modal-tabs .ant-tabs-content-holder { flex: 1; overflow: hidden; }
        .parameters-modal-tabs .ant-tabs-content { height: 100%; }
        .parameters-modal-tabs .ant-tabs-tabpane { height: 100%; overflow: hidden; }
        .parameters-modal-tabs .ant-form-item-tooltip { cursor: default !important; }
      `}</style>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ColumnPropertiesModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************

// Loads the unified SLA value for the merged SimpleTime field.
// Prefers business time when set, then elapsed time, with backward compat for old flat fields.
function loadSla(sla) {
  if (sla?.businessTime?.minutes > 0) {
    return { type: 'business', ...sla.businessTime };
  }
  if (sla?.elapsedTime?.minutes > 0) {
    return { type: 'elapsed', ...sla.elapsedTime };
  }
  // Backward compat: old flat format
  if (sla?.businessMinutes > 0) {
    return { type: 'business', value: sla.businessMinutes, unit: 'minutes', minutes: sla.businessMinutes };
  }
  if (sla?.elapsedHours > 0) {
    return { type: 'elapsed', value: sla.elapsedHours, unit: 'hours', minutes: sla.elapsedHours * 60 };
  }
  return { type: 'elapsed', value: 0, unit: 'hours', minutes: 0 };
}

// Splits the unified SLA value back into the two fields the backend expects.
function buildSlaPayload(sla) {
  const isEmpty = { value: 0, unit: 'hours', minutes: 0 };
  if (!sla) return { elapsedTime: isEmpty, businessTime: { ...isEmpty, unit: 'minutes' } };
  const { type, value, unit, minutes } = sla;
  const entry = { value: value ?? 0, unit: unit ?? 'hours', minutes: minutes ?? 0 };
  return {
    elapsedTime: type === 'elapsed' ? entry : { ...isEmpty },
    businessTime: type === 'business' ? entry : { ...isEmpty, unit: 'minutes' },
  };
}
